import { z } from "zod";
const money = z.number().finite().nullable();
/** Informative strategy economics. These amounts never enter ECC recognition,
 * carry, variation margin, approval snapshots or recognition schedules. */
export const BilateralPnlCellSchema = z.object({
    clearedEstimateEUR: money.optional(),
    physicalFinalEUR: money, physicalEstimateEUR: money,
    otcFinalEUR: money, otcEstimateEUR: money,
    physicalMissingCount: z.number().int().nonnegative(), otcMissingCount: z.number().int().nonnegative(),
    unclassifiedCount: z.number().int().nonnegative(), blockers: z.array(z.string()).optional(),
});
export const zeroBilateralPnlCell = () => ({ physicalFinalEUR: 0, physicalEstimateEUR: 0,
    otcFinalEUR: 0, otcEstimateEUR: 0, physicalMissingCount: 0, otcMissingCount: 0, unclassifiedCount: 0 });
export function sumBilateralPnlCells(cells) {
    const values = cells.filter((cell) => cell !== undefined);
    const sum = (key) => values.some(cell => cell[key] === null) ? null : values.reduce((total, cell) => total + Math.round((cell[key] ?? 0) * 100), 0) / 100;
    return { clearedEstimateEUR: sum("clearedEstimateEUR"), physicalFinalEUR: sum("physicalFinalEUR"), physicalEstimateEUR: sum("physicalEstimateEUR"),
        otcFinalEUR: sum("otcFinalEUR"), otcEstimateEUR: sum("otcEstimateEUR"),
        physicalMissingCount: values.reduce((s, c) => s + c.physicalMissingCount, 0),
        otcMissingCount: values.reduce((s, c) => s + c.otcMissingCount, 0),
        unclassifiedCount: values.reduce((s, c) => s + c.unclassifiedCount, 0),
        blockers: [...new Set(values.flatMap(c => c.blockers ?? []))] };
}
export function bilateralPnlAmount(cell, kind, includeEstimates = true) {
    const final = kind === "physical" ? cell.physicalFinalEUR : cell.otcFinalEUR;
    const estimate = kind === "physical" ? cell.physicalEstimateEUR : cell.otcEstimateEUR;
    return !includeEstimates ? final : final === null || estimate === null ? null : (Math.round(final * 100) + Math.round(estimate * 100)) / 100;
}
export const BilateralPnlSourceSchema = z.object({
    sourceKind: z.enum(["trade", "deal"]).optional(),
    tradeId: z.string().nullable(), dealId: z.string().nullable(), pair: z.string().nullable(), book: z.string().nullable(), trader: z.string().nullable(),
    product: z.string(), period: z.string(), date: z.string().nullable(),
    instrumentClass: z.enum(["physical", "otc_financial", "unclassified"]),
    cells: z.record(z.string(), BilateralPnlCellSchema),
    unallocatedReason: z.string().optional(),
}).superRefine((source, ctx) => {
    if (source.sourceKind === "deal" ? source.tradeId !== null || source.dealId === null : source.tradeId === null)
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "A source identifies a real projected trade or a canonical deal, never a fabricated execution." });
});
export const BilateralPnlReportSchema = z.object({ sources: z.array(BilateralPnlSourceSchema), notes: z.array(z.string()) });
/** An unplaceable delivery must remain visible and incomplete across a requested
 * range, without claiming that any profit is recognized in those months. */
export function bilateralSourceCells(source, months) {
    if (!source.unallocatedReason)
        return source.cells;
    return Object.fromEntries(months.map(month => {
        const cell = zeroBilateralPnlCell();
        cell.blockers = [source.unallocatedReason];
        if (source.instrumentClass === "physical") {
            cell.physicalEstimateEUR = null;
            cell.physicalMissingCount = 1;
        }
        else if (source.instrumentClass === "otc_financial") {
            cell.otcFinalEUR = null;
            cell.otcEstimateEUR = null;
            cell.otcMissingCount = 1;
        }
        else
            cell.unclassifiedCount = 1;
        return [month, cell];
    }));
}
//# sourceMappingURL=eex-bilateral-pnl.js.map